import { Star } from "lucide-react";

/**
 * PracticeByline — a restrained editorial meta strip appearing directly
 * beneath the hero on every practice-area subpage. Gives each page the
 * same "signed by the partner" gravitas as a Journal essay.
 *
 * Sits on an ivory band (so it always contrasts against the dark hero
 * that precedes it). All entries are optional except `minutes`.
 *
 * Usage:
 *   <PracticeByline
 *     minutes={7}
 *     lastReviewed="February 2026"
 *     discipline="Private Client"
 *   />
 */
export default function PracticeByline({
  minutes,
  lastReviewed,
  discipline,
  author = "Darryl V. Pratt",
  role = "Managing Partner",
}) {
  return (
    <div
      className="bg-ivory grain border-y border-brass/25"
      data-testid="practice-byline"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 md:py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-8">
          {/* Left rail — brass star, kicker, byline */}
          <div className="flex items-center gap-4">
            <Star size={11} className="text-brass fill-brass shrink-0" />
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                Signed by the Partner
              </span>
              <span
                className="font-cormorant italic text-navy text-[17px] md:text-lg leading-none"
                data-testid="practice-byline-author"
              >
                By {author}
              </span>
              <span className="text-[10px] uppercase tracking-[0.28em] text-slate2/70 font-medium">
                {role}
              </span>
            </div>
          </div>

          {/* Right rail — reading time · reviewed date · discipline pill */}
          <div className="flex flex-wrap items-center gap-3 md:gap-5">
            {typeof minutes === "number" && (
              <div
                className="text-[10px] uppercase tracking-[0.28em] text-slate2/80"
                data-testid="practice-byline-minutes"
              >
                {minutes} min read
              </div>
            )}
            {lastReviewed && (
              <>
                <span className="hidden md:inline text-brass/50 text-[10px]">·</span>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">
                  Reviewed {lastReviewed}
                </div>
              </>
            )}
            {discipline && (
              <>
                <span className="hidden md:inline text-brass/50 text-[10px]">·</span>
                <span
                  className="text-[10px] uppercase tracking-[0.28em] font-medium text-brass border border-brass/40 px-3 py-1"
                  data-testid="practice-byline-discipline"
                >
                  {discipline}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
